"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload } from "lucide-react";

function slugify(name: string) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

interface StepBrandBasicsProps {
  brandId: string;
  onContinue: () => void;
  onSkip: () => void;
  onBack: () => void;
}

export function StepBrandBasics({ brandId, onContinue, onSkip, onBack }: StepBrandBasicsProps) {
  const { t } = useTranslation(["wizard", "common"]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const slug = slugify(name);

  async function handleContinue() {
    if (!name.trim()) { onContinue(); return; }
    setSaving(true);
    try {
      await fetch(`/api/brands/${brandId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), slug }),
      });
    } finally {
      setSaving(false);
      onContinue();
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-[18px] font-bold text-foreground tracking-tight">{t("steps.brandBasics.title", { ns: "wizard" })}</h2>
        <p className="text-[13px] text-muted-foreground mt-1">{t("steps.brandBasics.description", { ns: "wizard" })}</p>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="brand-name">{t("steps.brandBasics.nameLabel", { ns: "wizard" })}</Label>
          <Input
            id="brand-name"
            placeholder={t("steps.brandBasics.namePlaceholder", { ns: "wizard" })}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>
            {t("steps.brandBasics.slugLabel", { ns: "wizard" })}{" "}
            <span className="text-muted-foreground font-normal">{t("steps.brandBasics.slugAuto", { ns: "wizard" })}</span>
          </Label>
          <div className="h-10 rounded-lg border border-border bg-muted px-3 flex items-center text-[14px] text-muted-foreground font-mono">
            {slug || t("steps.brandBasics.slugFallback", { ns: "wizard" })}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>
            {t("steps.brandBasics.logoLabel", { ns: "wizard" })}{" "}
            <span className="text-muted-foreground font-normal">{t("optional", { ns: "common" })}</span>
          </Label>
          <label
            htmlFor="logo-upload"
            className="h-[72px] rounded-lg border-[1.5px] border-dashed border-brand/40 bg-brand-subtle flex items-center justify-center gap-2 cursor-pointer hover:border-brand transition-colors"
          >
            <Upload size={16} className="text-brand" />
            <span className="text-[12px] font-medium text-brand">{t("steps.brandBasics.uploadLogo", { ns: "wizard" })}</span>
            <span className="text-[11px] text-muted-foreground">{t("steps.brandBasics.logoHint", { ns: "wizard" })}</span>
          </label>
          <input id="logo-upload" type="file" accept="image/png,image/svg+xml" className="sr-only" />
        </div>
      </div>

      <div className="flex items-center gap-2 mt-1">
        <Button variant="secondary" onClick={onBack}>{t("nav.back", { ns: "wizard" })}</Button>
        <Button className="flex-1" onClick={handleContinue} disabled={saving}>
          {saving ? t("actions.saving", { ns: "common" }) : t("nav.continue", { ns: "wizard" })}
        </Button>
        <Button variant="ghost" className="text-muted-foreground" onClick={onSkip}>
          {t("nav.skip", { ns: "wizard" })}
        </Button>
      </div>
    </div>
  );
}
